/**
 * FormRequiredFieldsModal
 *
 * Feedback surface for `checkRequiredFields`. Takes no props: it reads
 * `formStore.state.missingRequiredFields`, stays open exactly while that array
 * is non-empty, and empties it on close. Mounted once per form surface (the form
 * page and the layout-editor preview) so every gated action shares one dialog
 * without the action provider having to own it.
 *
 * Labels resolve here rather than in the gate — the field-type registry is a
 * React context, and keeping it out of the gate is what lets the gate stay a
 * plain function with no subscriptions.
 *
 * `collectMissing` does not dedupe, and an entity reference's group id shares a
 * namespace with field ids, so `fieldId` alone is not a unique key: the row key
 * is the reference/field kind, the id, and the occurrence position together.
 */

import { FC, useContext } from 'react';
import { Modal, Space, Typography, theme } from 'antd';
import { useSnapshot } from 'valtio';
import { formStore } from '@/store';
import { FieldTypeContext } from '../../providers/FieldTypeContext';
import FlexCol from '../atoms/FlexCol';

const { Text } = Typography;

const clearMissingRequiredFields = (): void => {
  formStore.state.missingRequiredFields = [];
};

export const FormRequiredFieldsModal: FC = () => {
  const { token } = theme.useToken();
  const { fieldTypes } = useContext(FieldTypeContext);
  const snap = useSnapshot(formStore.state);
  const missingFields = snap.missingRequiredFields;

  return (
    <Modal
      title="Missing Required Fields"
      open={missingFields.length > 0}
      onOk={clearMissingRequiredFields}
      onCancel={clearMissingRequiredFields}
      okText="Got it"
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      <Space vertical style={{ width: '100%' }} size="middle">
        <Text>Before you can proceed, fill in the following fields:</Text>
        <FlexCol noGap>
          {missingFields.map((field, index) => (
            <div
              key={`${field.isEntityReference ? 'ref' : 'field'}:${field.fieldId}:${index}`}
              style={{
                paddingBlock: token.paddingXS,
                borderBottom: index < missingFields.length - 1
                  ? `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`
                  : 'none',
              }}
            >
              <Space>
                <Text strong>
                  {field.isEntityReference
                    ? `${field.entityId} Record`
                    : fieldTypes[field.fieldId]?.description?.shortLabel || field.fieldId}
                </Text>
                {!field.isVisibleInCurrentMode && (
                  <Text type="warning">
                    Switch to Internal view to access
                  </Text>
                )}
              </Space>
            </div>
          ))}
        </FlexCol>
      </Space>
    </Modal>
  );
};

export default FormRequiredFieldsModal;
